import _pick from "lodash/pick";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";

import { ExclamationIcon } from "@heroicons/react/outline";
import DashboardWrapper from "components/lib/Global/DashboardWrapper";
import Hero from "components/lib/Global/Hero";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { Constants } from "config/constants";
import { format } from "date-fns";
import { useToast } from "hooks/useToast";
import { verifyDiscountCodeAsync } from "state/slices/discount";
import { generateOrderIdAsync } from "state/slices/order";
import { getPackagesAsync } from "state/slices/package";
import {
  cancelTransactionAsync,
  checkTransactionStatusAsync,
  initiateCheckoutAsync,
  verifyPaymentAsync,
} from "state/slices/payment";
import { updateUser } from "state/slices/user";
import { classNames, delay, getFullName } from "utils";
import PaymentSummary from "./PaymentSummary";
import PaystackPopup from "./PaystackPopup";
import PreferredPlan from "./PreferredPlan";
import PaymentMethod from "./PaymentMethod";
import PaystackPaymentMethod from "./PaystackPaymentMethod";

const pages = [
  {
    name: "Select your package",
    href: "/#package-selection",
    current: false,
  },
  { name: "Register your business", href: "#", current: false },
  {
    name: "Status",
    href: "#",
    current: true,
  },
];

function Checkout({ self, companyType, company, organization }) {
  const [fetchingPackage, setFetchingPackage] = useState(true);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [submittingToCheckout, setSubmittingToCheckout] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [paymentResponse, setPaymentResponse] = useState(false);
  const [transactionToken, setTransactionToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canRetryPayment, setCanRetryPayment] = useState(false);
  const [checkoutPayload, setCheckoutPayload] = useState(false);
  const [checkoutResponse, setCheckoutResponse] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const history = useHistory();

  const { packages } = useSelector((state) => state.packageSlice);
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const userSlice = useSelector((state) => state.userSlice);

  const { pendingPaidApplicationsCount } = packages;

  const dispatch = useDispatch();
  const { toast } = useToast();

  const navigation = [
    {
      id: "preferredPlan",
      name: "1. Preferred plan",
    },
    {
      id: "paymentMethod",
      name: "2. Payment method",
    },
    {
      id: "paymentSummary",
      name: "3. Payment summary",
    },
  ];

  const [selectedTab, setSelectedTab] = useState(navigation[0]);
  const abbrevCompanyType = Constants.COMPANY_NAME_ABBREVIATION[companyType];

  useEffect(async () => {
    await dispatch(
      getPackagesAsync({
        service:
          companyType === "Private Company"
            ? "Private Limited Company"
            : companyType,
      })
    );
    setFetchingPackage(false);
  }, [companyType, abbrevCompanyType]);

  const tabs = useMemo(() => {
    return packages.items
      .map((pricingPackage) => ({
        id: pricingPackage._id,
        label: pricingPackage.name,
      }))
      .sort((a, b) => {
        const order = ["Basic", "Print", "Premium"];

        // Get the index of each object's label in the order array
        const indexA = order.indexOf(a.label);
        const indexB = order.indexOf(b.label);

        // Compare the indices and return the comparison result
        return indexA - indexB;
      });
  }, [packages.items]);

  const selectedPackage = packages.items.find(
    (pricingPackage) => pricingPackage._id === tabs[selectedTabIndex].id
  );

  if (fetchingPackage || getUserStatus.status === "loading") {
    return (
      <DashboardWrapper showBreadCrumbs pages={pages}>
        <div className="flex justify-center mx-auto mt-4 text-white">
          <SpinnerIcon className="text-gray-400" />
        </div>
      </DashboardWrapper>
    );
  }

  if (packages.totalCount === 0) {
    return (
      <DashboardWrapper showBreadCrumbs pages={pages}>
        <div className="flex justify-center mx-auto mt-4">
          No Pricing package found
        </div>
      </DashboardWrapper>
    );
  }

  const handleProceedToCheckout = async (selectedPackage) => {
    try {
      setSubmittingToCheckout(true);
      if (promoCode) {
        const response = await dispatch(
          verifyDiscountCodeAsync({
            code: promoCode,
            package: selectedPackage._id,
            self,
            company,
            organization,
          })
        );
        if (response.error) {
          toast(
            "error",
            response.payload?.messageText ??
              "Something went wrong please try again"
          );
          return;
        }
        setDiscountPercentage(response.payload.discount);
      }
      setSelectedTab(navigation[1]);
    } catch (error) {
      toast("error", error?.message ?? "Something went wrong please try again");
    } finally {
      setSubmittingToCheckout(false);
    }
  };

  const handleGoBack = (tab) => {
    if (tab === 0) {
      setDiscountPercentage(0);
      setPromoCode("");
    }
    setSelectedTab(navigation[tab]);
  };

  const sflData = Constants.SFL_DATA;

  const handleMpesaPaymentResponse = async (response, payload) => {
    setPaymentResponse(response.payload.message);
    setTransactionToken(response.payload.transactionToken);

    // wait for 40 seconds before checking the transaction status
    await delay(40000);

    const MAX_DURATION = 25000; // 25 seconds
    const startTime = new Date().getTime();
    let isTransactionPending = true;

    while (isTransactionPending) {
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - startTime;
      if (elapsedTime >= MAX_DURATION) {
        // Break out of the loop if the maximum duration is reached
        isTransactionPending = false;
        setCanRetryPayment(true);
        toast("error", "Something went wrong please try again");
        break;
      }

      const transactionStatusResponse = await dispatch(
        checkTransactionStatusAsync({
          data: { transactionToken: response.payload.transactionToken },
        })
      );

      if (transactionStatusResponse.payload.status) {
        isTransactionPending = false;
        const verifyPaymentResponse = await dispatch(
          verifyPaymentAsync({
            data: {
              TransID: response.payload.transactionToken,
              transRef: response.payload.transRef,
              payload,
            },
          })
        );
        const { redirectUrl } = verifyPaymentResponse.payload;
        history.push(redirectUrl);
      } else if (!transactionStatusResponse.payload.reconfirm) {
        isTransactionPending = false;
        setTransactionToken(null);
        setPaymentResponse(false);
        toast(
          "error",
          transactionStatusResponse.payload?.message ??
            "Something went wrong please try again"
        );
        setIsSubmitting(false);
      } else {
        await delay(5000);
      }
    }
    setIsSubmitting(false);
  };

  const handleCardPaymentResponse = async (response, payload) => {
    const verifyPaymentResponse = await dispatch(
      verifyPaymentAsync({
        data: {
          TransID: response.payload.transactionToken,
          transRef: response.payload.transRef,
          payload,
        },
      })
    );
    const { redirectUrl } = verifyPaymentResponse.payload;
    history.push(redirectUrl);
    setIsSubmitting(false);
  };

  const handleMakePayment = async (actualAmount, discountAmount, values) => {
    const amountToBeCharged = actualAmount - discountAmount;
    setCanRetryPayment(false);
    setPaymentResponse(false);
    setIsSubmitting(true);
    const { paymentMethod, mpesaPhoneNumber } = values;

    const invoice = {
      plan: selectedPackage.name,
      billFrom: {
        name: sflData.name,
        address: `${sflData.buildingName}, ${sflData.floorNumber}, ${sflData.streetName}, ${sflData.location.county}`,
        pin: sflData.pin,
      },
      billTo: {
        name: getFullName(userSlice.names),
        address: values.billingAddress,
        pin: userSlice.identificationDetails?.KRAPin,
      },
      items: [
        {
          description: selectedPackage.product.description,
          quantity: 1,
          actualAmount,
          discountAmount,
          rate: amountToBeCharged,
        },
      ],
      currency: selectedCurrency.type,
      isTaxApplicable: selectedCurrency.applicableTax > 1,
      applicableTax: selectedCurrency.applicableTax,
      paymentMethod,
      ..._pick(values, ["firstName", "email"]),
    };

    const generateOrderIdResponse = await dispatch(
      generateOrderIdAsync({ type: abbrevCompanyType })
    );

    const orderId = generateOrderIdResponse.payload.orderId;

    const payload = {
      company: { type: abbrevCompanyType },
      orderId,
      invoice,
    };
    setCheckoutPayload(payload);
    dispatch(updateUser({ id: userSlice._id, data: { mpesaPhoneNumber } }));
    const response = await dispatch(
      initiateCheckoutAsync({
        data: {
          amount: parseFloat(amountToBeCharged.toFixed(2)),
          currency: selectedCurrency.type,
          customer: {
            ...values,
            country: userSlice.residentialAddressDetails.country || "KE",
          },
          serviceType: Constants.DPO.SERVICE_TYPES.COMPANY_REGISTRATION,
          serviceDescription: selectedPackage.product.description,
          paymentMethod,
          mpesaPhoneNumber,
          ...values,
          expiryDate: values.expiryDate
            ? format(new Date(values.expiryDate), "MMyy")
            : "",
        },
      })
    );
    setCheckoutResponse(response);
    if (response.payload.status) {
      if (paymentMethod === "mpesa") {
        handleMpesaPaymentResponse(response, payload);
      } else if (paymentMethod === "card") {
        handleCardPaymentResponse(response, payload);
      }
    } else {
      toast(
        "error",
        response.payload?.message ?? "Something went wrong please try again"
      );
      setIsSubmitting(false);
    }
  };

  const handleCancelTransaction = async (transactionToken) => {
    await dispatch(
      cancelTransactionAsync({
        data: { transactionToken },
      })
    );
    setTransactionToken(null);
    setPaymentResponse(false);
    setCanRetryPayment(false);
    setIsSubmitting(false);
  };

  const handleRetryPayment = async () => {
    setIsRetrying(true);
    const MAX_TRIES = 6; // 6 tries which should take around 30 seconds
    let retryCount = 0;
    let isTransactionPending = true;

    while (isTransactionPending) {
      if (retryCount >= MAX_TRIES) {
        // Break out of the loop if the maximum duration is reached
        isTransactionPending = false;
        handleCancelTransaction(checkoutResponse.payload.transactionToken);
        setCanRetryPayment(false);
        toast("error", "Something went wrong please try again");
        break;
      }

      const transactionStatusResponse = await dispatch(
        checkTransactionStatusAsync({
          data: { transactionToken: checkoutResponse.payload.transactionToken },
        })
      );

      if (transactionStatusResponse.payload.status) {
        isTransactionPending = false;
        const verifyPaymentResponse = await dispatch(
          verifyPaymentAsync({
            data: {
              TransID: checkoutResponse.payload.transactionToken,
              transRef: checkoutResponse.payload.transRef,
              payload: checkoutPayload,
            },
          })
        );
        const { redirectUrl } = verifyPaymentResponse.payload;
        history.push(redirectUrl);
      } else if (!transactionStatusResponse.payload.reconfirm) {
        isTransactionPending = false;
        setCanRetryPayment(false);
        setTransactionToken(null);
        setPaymentResponse(false);
        toast(
          "error",
          transactionStatusResponse.payload?.message ??
            "Something went wrong please try again"
        );
        setIsRetrying(false);
      } else {
        await delay(5000);
        retryCount++;
      }
    }
    setIsRetrying(false);
  };

  return (
    <DashboardWrapper showBreadCrumbs pages={pages}>
      <div className="grid gap-10 py-10">
        <Hero
          heading={"Checkout"}
          description="
            Please select your mode of payment."
        />
        <div className="grid grid-cols-6 border-t border-gray">
          <div className="col-span-1 pr-8 pt-14">
            <p className="mb-4 text-sflPrimary">Select you plan:</p>
            <ul>
              {navigation.map((item) => (
                <li
                  key={item.name}
                  className={classNames(
                    item.id === selectedTab.id
                      ? "text-sflPrimary"
                      : "text-tertiary",
                    `mb-4 font-medium`
                  )}
                >
                  <span className="relative truncate">{item.name}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-5 md:border-l md:pl-11 pt-14 border-gray">
            {pendingPaidApplicationsCount > 0 && (
              <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationIcon
                      className="h-5 w-5 text-yellow-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      You already have {pendingPaidApplicationsCount} paid
                      pending{" "}
                      <span className="font-medium text-yellow-700">
                        {companyType}
                      </span>{" "}
                      {pendingPaidApplicationsCount === 1
                        ? "application"
                        : "applications"}
                      . You can go to{" "}
                      <Link
                        to="/transactions"
                        className="font-medium text-yellow-700 underline hover:text-yellow-600"
                      >
                        transactions
                      </Link>{" "}
                      to complete and submit{" "}
                      {pendingPaidApplicationsCount === 1 ? "it" : "them"}.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {selectedTab?.id === "preferredPlan" && (
              <PreferredPlan
                tabs={tabs}
                selectedPackage={selectedPackage}
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={setSelectedTabIndex}
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                submittingToCheckout={submittingToCheckout}
                handleProceedToCheckout={handleProceedToCheckout}
              />
            )}
            {selectedTab?.id === "paymentMethod" && (
              <>
                <PaystackPaymentMethod
                  companyType={companyType}
                  discountPercentage={discountPercentage}
                  selectedPackage={selectedPackage}
                  selectedCurrency={selectedCurrency}
                  handleGoBack={handleGoBack}
                  abbrevCompanyType={abbrevCompanyType}
                />
                {/* <PaymentMethod
                  companyType={companyType}
                  discountPercentage={discountPercentage}
                  selectedPackage={selectedPackage}
                  selectedCurrency={selectedCurrency}
                  paymentResponse={paymentResponse}
                  transactionToken={transactionToken}
                  isSubmitting={isSubmitting}
                  canRetryPayment={canRetryPayment}
                  isRetrying={isRetrying}
                  handleMakePayment={handleMakePayment}
                  handleGoBack={handleGoBack}
                  handleCancelTransaction={handleCancelTransaction}
                  handleRetryPayment={handleRetryPayment}
                /> */}
              </>
            )}
            {selectedTab?.id === "paymentSummary" && <PaymentSummary />}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}

export default Checkout;
